<script setup>
import { ref } from "vue";
import useCartStore from "../../store/modules/cart";
import { Vue3SlideUpDown } from "vue3-slide-up-down";
import ITicket from "../ITicket/ITicket.vue";
import ILink from "../ILink/ILink.vue";
import ITeams from "../ITeams/ITeams.vue";
import { dateHelper } from "@shop/frontend/web/js/helpers/dateHelper";
import { translator } from "@shop/frontend/web/js/services/TranslatorService";

translator.getLanguages();

const cartStore = useCartStore();
const props = defineProps({
	itemCase: {
		type: Object,
		required: true,
	},
	trashButton: {
		type: Boolean,
		default: true,
	},
});

const isEventsListOpen = ref(true);

function removeEventFromSubscription(subscriptionEventObject) {
	cartStore.removeEventFromSubscription(subscriptionEventObject, props.itemCase.orderId);
}

function removeOrder(orderId) {
	cartStore.removeOrderById(orderId);
}

function changeLoyalty(ticket, eventId, categoryId, priceId) {
	cartStore.changeLoyalty(ticket, eventId, props.itemCase.orderId, categoryId, priceId);
}
</script>

<template>
	<div class="i-order-subscribe-item__sub-wrapper">
		<a
			class="i-order-subscribe-item__sub-link"
			:href="'/subscription/' + itemCase.subscriptionId"
		>
			{{ itemCase.name }}
		</a>
		<ILink
			v-if="trashButton"
			:icon-name="'trash'"
			class="trash-btn"
			@click="removeOrder(itemCase.orderId)"
		>
			<span class="desktop">{{ translator.t('main', 'Remove') }}</span>
		</ILink>
	</div>
	<button
		class="i-order-subscribe-item__btn-events-open"
		@click="isEventsListOpen = !isEventsListOpen"
	>
		{{ translator.t('main', 'Number of season tickets') }}:
		<strong class="mr-1">{{ itemCase.eventsCount }}</strong>
		<svg
			class="i-order-subscribe-item__arrow-svg"
			:class="{ 'i-order-event-item__arrow-svg_active': isEventsListOpen }"
		>
			<use xlink:href="#down"></use>
		</svg>
	</button>
	<vue3-slide-up-down class="slider" v-model="isEventsListOpen">
		<div
			class="i-order-subscribe-item__sub"
			v-for="(item, index) in itemCase.events"
			:key="index"
		>
			<div class="i-order-subscribe-item__top-wrapper">
				<ITeams
					v-if="item.teams"
					:first-team="{
						...item.teams[0],
						imageUrl: item.images.home_team,
					}"
					:second-team="{
						...item.teams[1],
						imageUrl: item.images.guest_team,
					}"
					:is-home-logo-with-opponent="item.isHomeLogoWithOpponent"
				/>
				<ITeams
					v-else-if="item?.images?.event_image"
					:first-team="{
						imageUrl: item?.images?.event_image,
					}"
					:name="item?.name"
					:is-home-logo-with-opponent="item.isHomeLogoWithOpponent"
				/>
				<div v-else class="i-order-subscribe-item__sub-name">
					<div class="i-order-subscribe-item__sub-title">
						{{ item.name }}
					</div>
				</div>
				<div class="i-order-subscribe-item__top-right">
					<div class="desktop">
						{{ translator.t('main', 'Price') }}: <strong>{{ item.summ }} ₽</strong>
					</div>
					<ILink
						v-if="trashButton"
						:icon-name="'close'"
						class="close-btn"
						@click="
							removeEventFromSubscription({
								eventId: itemCase.subscriptionId,
								subEventId: index,
							})
						"
					/>
				</div>
			</div>
			<div class="i-order-subscribe-item__details">
				<span class="time">
					<svg>
						<use xlink:href="#calendar"></use>
					</svg>
					{{ dateHelper.getFormattedDate(item.date, 'DD.MM.YYYY dd HH:mm') }}
				</span>
				<span class="place">
					<svg>
						<use xlink:href="#location"></use>
					</svg>
					{{ item.place }}
				</span>
			</div>
			<div class="i-order-subscribe-item__tickets">
				<div v-for="ticket in item.carts" :key="ticket.place" class="tickets-item">
					<i-ticket
						:ticket="ticket"
						:inactive="true"
						:short="true"
						@change-loyalty="
							({ ticket, categoryId, priceId }) =>
								changeLoyalty(ticket, itemCase.id, categoryId, priceId)
						"
					/>
				</div>
			</div>
			<div class="mobile">
				{{ translator.t('main', 'Price') }}: <strong>{{ item.summ }} ₽</strong>
			</div>
		</div>
	</vue3-slide-up-down>
</template>

<style scoped lang="scss">
@import "frontend/web/src/styles/utils/variables.scss";
@import "./i-order-subscribe-item.scss";
</style>
